import React, { useEffect, useMemo, useState } from 'react'
import { Arr } from '../../../../../helpers/Arr'
import { PopupType } from '../../../Popup/Popup'
import { usePopup } from '../../../Popup/PopupContext'
import './CsvTable.scss'

export interface Row {
  value: string | undefined
  copy: boolean
  download?: () => void
}

export interface CsvTableRow {
  row: {
    [span: number]: Row
  }
  colspan?: number
  disabled?: boolean
  copy?: boolean
}

export interface CsvTableSection {
  /** Section name */
  name?: string
  /** Section icon */
  icon?: string
  /**  List of rows in this section */
  rows: {
    [id: number]: CsvTableRow
  }
  /**  Section is collapsed by default */
  collapsed?: boolean
  /**
   * Count of columns in this section.
   * If count of columns is less, then
   * is used section with dropdown
   */
  columnCount: number
}

interface ICsvTableProps {
  columnCount: number
  sections: CsvTableSection[]
  showEmptyFields?: boolean
}

const objArrToArr = <T,>(obj: object): T[] => {
  return Object.values(obj) as T[]
}

/**
 * Table divided into sections
 */
const CsvTable = (props: ICsvTableProps) => {
  const [table, setTable] = useState<CsvTableSection[]>()
  const popup = usePopup()

  useMemo(() => {
    if (props.sections.length > 0) {
      setTable(props.sections)
    }
  }, [props.sections])

  const toggleSection = (section: CsvTableSection) => {
    const newTable = [...(table ?? [])]
    const selectedSection = newTable.findIndex((s) => s === section)

    newTable[selectedSection].collapsed = !newTable[selectedSection].collapsed

    setTable(newTable)
  }

  useEffect(() => {
    if (!props.sections.length || table?.find((t) => Object.keys(t.rows).find((r) => !!t.rows[parseInt(r)].colspan))) {
      return
    }

    // Maximal column amount
    const amount =
      table
        ?.map((s) => Object.values(objArrToArr<CsvTableRow>(s.rows).sort((r) => Object.values(r).length)[0].row).length)
        ?.reduce((a, b) => Math.max(a, b), -Infinity) || 0

    let newTable: CsvTableSection[] | undefined = []

    // Setting up disabled rows
    newTable = table?.filter((s) => {
      objArrToArr<CsvTableRow>(s.rows).map((rows) => {
        if (rows.row) {
          rows.disabled = !rows?.row[2].value
        } else {
          rows.disabled = true
        }
      })

      return s
    })

    // Setting up copy icons
    newTable = newTable?.filter((s) => {
      objArrToArr<CsvTableRow>(s.rows).map((row) => {
        Object.values(row.row).map((value, i) => {
          if (i > 0 && value && Object.values(row.row).length === 2) {
            row.copy = true
          }
        })
      })

      return s
    })

    // Setting up colspans
    newTable = newTable?.filter((s) => {
      objArrToArr<CsvTableRow>(s.rows).map((rows) => {
        rows.colspan = amount
      })

      return s
    })

    setTable(newTable)
  }, [table])

  /**
   * Dropdown section header
   * @param section Table section
   * @returns Section header
   */
  const dropdownHeader = (section: CsvTableSection) => {
    const onClick = () => toggleSection(section)

    return (
      <td colSpan={props.columnCount + 1}>
        <div>
          <div className="left">
            <i className={section.icon}></i>
            {section.name}
          </div>
          <div className={['right', section.collapsed ? 'collapsed' : ''].join(' ')} onClick={onClick}>
            <i className="icon-bottom"></i>
          </div>
        </div>
      </td>
    )
  }

  /**
   * Default section header
   * @param section Table section
   * @returns Section header
   */
  const defaultHeader = (section: CsvTableSection) => {
    // Using first row as header
    const firstRow = Arr.firstOrDefault(objArrToArr<CsvTableRow>(section.rows))

    return (
      <>
        {Object.values(firstRow?.row ?? {}).map((value, index) => (
          <td key={index} colSpan={Object.values(firstRow?.row ?? {}).indexOf(value)}>
            {value.value}
          </td>
        ))}
      </>
    )
  }

  const writeToClipboard = (value: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        popup?.pushPopup('Value has been copied', PopupType.Success)
      })
      .catch(() => {
        popup?.pushPopup('Error', PopupType.Error)
      })
  }

  const drawTable = (
    <table className="csv-table">
      <tbody>
        {table?.map((section, index) => (
          <React.Fragment key={index}>
            <tr className="head">
              {props.columnCount !== section.columnCount ? dropdownHeader(section) : defaultHeader(section)}
            </tr>
            {!section.collapsed &&
              objArrToArr<CsvTableRow>(section.rows)
                .slice(props.columnCount !== section.columnCount ? 0 : 1)
                .map(
                  (row, index) =>
                    (props.showEmptyFields || !!row.row[2]?.value) && (
                      <tr key={index}>
                        {Object.values(row.row).map((value, i) => (
                          <React.Fragment key={i}>
                            <td
                              key={i}
                              colSpan={Object.values(row.row).length === i + 1 ? row.colspan : 1}
                              className={row.disabled ? 'disabled' : ''}
                            >
                              <div className="value">
                                <span>{value.value}</span>
                                {value.download && (
                                  <button onClick={() => value.download && value.download()}>Download</button>
                                )}
                                {!row.disabled && value.copy && (
                                  <i
                                    className="icon-copy"
                                    onClick={() => value.value && writeToClipboard(value.value)}
                                  ></i>
                                )}
                              </div>
                            </td>
                          </React.Fragment>
                        ))}
                      </tr>
                    )
                )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  )

  return drawTable
}

export default CsvTable
